import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import PreviewNoPHIBanner, { PREVIEW_MODE } from "../components/PreviewNoPHIBanner";
import { api } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, Search, ArrowUpRight, Loader2 } from "lucide-react";

export default function Patients() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/patients", { params: { q: q || undefined } });
      setItems(r.data);
    } catch (_) {
      toast.error("Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout>
      {PREVIEW_MODE && (
        <div className="mb-6">
          <PreviewNoPHIBanner />
        </div>
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="kicker">Patient Records</div>
          <h1 className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2" data-testid="patients-title">
            Your patients
          </h1>
          <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
            Imaging, medical profile, and case history in one place.
          </p>
        </div>
        {PREVIEW_MODE ? (
          <span
            data-testid="patients-new-preview-disabled"
            className="flex items-center gap-2 bg-[var(--pa-bg-2)] border border-[var(--pa-border)] text-[var(--pa-text-dim)] font-semibold px-5 py-2.5 rounded-md cursor-not-allowed"
            aria-disabled="true"
            title="Preview — new patient records are disabled"
          >
            <UserPlus size={16} /> New patient (Preview — disabled)
          </span>
        ) : (
          <Link
            to="/patients/new"
            data-testid="patients-new"
            className="flex items-center gap-2 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-5 py-2.5 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
          >
            <UserPlus size={16} /> New patient
          </Link>
        )}
      </div>

      <div className="gold-divider my-8" />

      <div className="flex gap-2 max-w-md mb-6">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-3 text-[var(--pa-text-dim)]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            data-testid="patients-search"
            onKeyDown={(e) => e.key === "Enter" && load()}
            className="w-full bg-[var(--pa-bg-2)] border border-[var(--pa-border)] rounded-md text-[var(--pa-text)] text-sm pl-9 pr-3 py-2 focus:border-[var(--pa-gold)] focus:outline-none"
          />
        </div>
        <button
          onClick={load}
          data-testid="patients-search-go"
          className="bg-[var(--pa-gold)] text-[var(--pa-bg)] text-sm font-semibold rounded-md px-4 hover:bg-[var(--pa-gold-hover)] transition"
        >
          Go
        </button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--pa-text-muted)]">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div
          data-testid="patients-empty-state"
          className="border border-dashed border-[var(--pa-border)] rounded-lg p-12 text-center text-sm text-[var(--pa-text-muted)]"
        >
          {PREVIEW_MODE ? (
            <>
              <div className="font-semibold text-[var(--pa-text)] mb-1">
                Patient records are disabled in Preview
              </div>
              <div>
                ARIA can walk you through the patient workflow, but no records
                will be created, saved, or displayed during beta testing.
              </div>
            </>
          ) : (
            <>
              No patients yet.{" "}
              <Link to="/patients/new" className="text-[var(--pa-gold)] underline">
                Create your first patient
              </Link>{" "}
              to start uploading imaging.
            </>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/patients/${p.id}`}
              data-testid={`patient-card-${p.id}`}
              className="module-card bg-[var(--pa-surface)] border border-[var(--pa-border)] rounded-lg p-5 transition-all duration-300 group"
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-serif text-lg text-[var(--pa-text)]">{p.name}</div>
                  <div className="text-xs text-[var(--pa-text-dim)] mt-1">
                    {p.dob ? `DOB ${p.dob}` : "DOB not set"} · {p.sex || "—"}
                  </div>
                </div>
                <ArrowUpRight
                  size={16}
                  className="text-[var(--pa-text-dim)] group-hover:text-[var(--pa-gold)] transition"
                />
              </div>
              {p.medical_conditions && (
                <div className="mt-4 text-xs text-[var(--pa-text-muted)] line-clamp-2">
                  <span className="text-[var(--pa-gold)]">Medical:</span> {p.medical_conditions}
                </div>
              )}
              {p.asa_classification && (
                <div className="mt-2 inline-block kicker text-[var(--pa-gold)]">
                  {p.asa_classification}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </Layout>
  );
}
